import type { CompanyAddressDisplaySource } from "@/modules/clients/clientAddressUtils";

export const ACCOUNTS_STATUS_FILTER_KEYS = [
  "is_client@eq",
  "primary_contact_status@eq",
] as const;

export const ACCOUNTS_LOCATION_FILTER_KEYS = [
  "state_abbr@eq",
  "city@eq",
] as const;

/** Not a DB column — dataProvider resolves via deals.project_type = website. */
export const ACCOUNTS_HAS_WEBSITE_PROJECT_FILTER_KEY =
  "has_website_project@eq" as const;

export const ACCOUNTS_PROJECT_FILTER_KEYS = [
  ACCOUNTS_HAS_WEBSITE_PROJECT_FILTER_KEY,
] as const;

export type AccountsStatusFilter = "all" | "clients" | "leads" | "prospects";

export type AccountsWebsiteProjectFilter = "all" | "website";

/** Compact city/state for Accounts table (e.g. Houston, TX). */
export const formatCompanyCityState = (
  company: CompanyAddressDisplaySource,
): string => {
  const city = company.city?.trim() ?? "";
  const state = company.state_abbr?.trim() ?? "";
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  if (state) return state;
  return "—";
};

export const getAccountsStatusFilter = (
  filterValues: Record<string, unknown> | undefined,
): AccountsStatusFilter => {
  if (filterValues?.["is_client@eq"] === true) return "clients";
  if (
    filterValues?.["is_client@eq"] === false &&
    filterValues?.["primary_contact_status@eq"] === "lead"
  ) {
    return "leads";
  }
  if (
    filterValues?.["is_client@eq"] === false &&
    filterValues?.["primary_contact_status@eq"] === "prospect"
  ) {
    return "prospects";
  }
  return "all";
};

export const applyAccountsStatusFilter = (
  filterValues: Record<string, unknown>,
  status: AccountsStatusFilter,
): Record<string, unknown> => {
  const next = { ...filterValues };
  for (const key of ACCOUNTS_STATUS_FILTER_KEYS) {
    delete next[key];
  }
  if (status === "clients") {
    next["is_client@eq"] = true;
  } else if (status === "leads") {
    next["is_client@eq"] = false;
    next["primary_contact_status@eq"] = "lead";
  } else if (status === "prospects") {
    next["is_client@eq"] = false;
    next["primary_contact_status@eq"] = "prospect";
  }
  return next;
};

export const getAccountsWebsiteProjectFilter = (
  filterValues: Record<string, unknown> | undefined,
): AccountsWebsiteProjectFilter => {
  if (filterValues?.[ACCOUNTS_HAS_WEBSITE_PROJECT_FILTER_KEY] === true) {
    return "website";
  }
  return "all";
};

export const applyAccountsWebsiteProjectFilter = (
  filterValues: Record<string, unknown>,
  value: AccountsWebsiteProjectFilter,
): Record<string, unknown> => {
  const next = { ...filterValues };
  delete next[ACCOUNTS_HAS_WEBSITE_PROJECT_FILTER_KEY];
  if (value === "website") {
    next[ACCOUNTS_HAS_WEBSITE_PROJECT_FILTER_KEY] = true;
  }
  return next;
};

/** Strip UI-only keys before PostgREST; return whether website-project facet is on. */
export const takeAccountsWebsiteProjectFilter = (
  filter: Record<string, unknown> | undefined,
): {
  wantsWebsiteProject: boolean;
  rest: Record<string, unknown>;
} => {
  const rest = { ...(filter ?? {}) };
  const wantsWebsiteProject =
    rest[ACCOUNTS_HAS_WEBSITE_PROJECT_FILTER_KEY] === true;
  delete rest[ACCOUNTS_HAS_WEBSITE_PROJECT_FILTER_KEY];
  return { wantsWebsiteProject, rest };
};

export const buildCompanyIdInFilter = (ids: Array<string | number>) =>
  `(${ids.map((id) => String(id)).join(",")})`;

export const hasAccountsListFilters = (
  filterValues: Record<string, unknown> | undefined,
): boolean => {
  if (!filterValues) return false;
  if (String(filterValues.q ?? "").trim()) return true;
  for (const key of [
    ...ACCOUNTS_STATUS_FILTER_KEYS,
    ...ACCOUNTS_LOCATION_FILTER_KEYS,
    ...ACCOUNTS_PROJECT_FILTER_KEYS,
  ]) {
    if (filterValues[key] != null && filterValues[key] !== "") return true;
  }
  return false;
};

export const clearAccountsFacetFilters = (
  filterValues: Record<string, unknown>,
): Record<string, unknown> => {
  const next = { ...filterValues };
  for (const key of [
    ...ACCOUNTS_STATUS_FILTER_KEYS,
    ...ACCOUNTS_LOCATION_FILTER_KEYS,
    ...ACCOUNTS_PROJECT_FILTER_KEYS,
  ]) {
    delete next[key];
  }
  return next;
};

export const uniqueSortedStrings = (
  values: Array<string | null | undefined>,
) => {
  const byLower = new Map<string, string>();
  for (const value of values) {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (!byLower.has(key)) byLower.set(key, trimmed);
  }
  return Array.from(byLower.values()).sort((a, b) => a.localeCompare(b));
};
